import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, Sparkles, SendHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '@/lib/store';

const SUGGESTIONS = [
  'More advice, less small talk',
  'Shorter punchy clips only',
  'Hard truths and turning points',
  'Keep the strongest emotional moments',
];

export default function HighlightRefine() {
  const { state, refineCuts } = useStore();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const messages = state.refineMessages || [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages.length, busy]);

  const onRebuild = async (text) => {
    const prompt = (text ?? draft).trim();
    if (!prompt || busy) return;
    setBusy(true);
    setDraft('');
    try {
      const ok = await refineCuts(prompt);
      if (ok) {
        toast.success('Cuts rebuilt', {
          description: 'Highlights reordered to match your preference.',
        });
      } else {
        toast.error('Could not rebuild cuts', {
          description: 'Run an extraction first, then refine.',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-panel-soft overflow-hidden" data-testid="refine-panel">
      <div className="px-5 sm:px-6 pt-5 pb-3 border-b border-white/8 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
            Refine · preference chat
          </div>
          <h3 className="font-display font-semibold text-xl text-white">
            Not happy with these cuts?
          </h3>
          <p className="text-sm text-white/55 mt-1 max-w-xl leading-relaxed">
            Tell SIMNOPSIS what you want more of — tone, theme, or length — and rebuild the highlight set from this recording.
          </p>
        </div>
        <Sparkles className="h-5 w-5 text-[#7EE06D] shrink-0 mt-1" />
      </div>

      <div className="px-5 sm:px-6 py-4 space-y-3 max-h-[320px] overflow-y-auto">
        {messages.length === 0 && (
          <div className="rounded-2xl bg-white/[0.03] border border-white/8 px-4 py-3 text-sm text-white/65 leading-relaxed">
            Describe the cuts you want. Example: “Focus on hard truths and keep clips under a minute.”
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={`${m.role}-${i}-${m.at || i}`}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-gradient-to-r from-[#20B8F2]/25 to-[#7EE06D]/20 border border-white/15 text-white'
                  : 'bg-white/[0.04] border border-white/8 text-white/75'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-2.5 text-sm text-white/50 border border-white/8 bg-white/[0.03] font-mono text-[11px] uppercase tracking-widest">
              Rebuilding cuts…
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-5 sm:px-6 pb-3 flex flex-wrap gap-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={busy}
            onClick={() => onRebuild(s)}
            className="pill !normal-case !tracking-normal !text-[11px] hover:border-white/20 hover:text-white/85 transition-colors disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="p-4 sm:p-5 border-t border-white/8">
        <div className="relative inner-dark !border !border-white/20 rounded-2xl overflow-hidden">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onRebuild();
              }
            }}
            rows={3}
            placeholder="What should these audio cuts focus on?"
            className="w-full bg-transparent border-0 outline-none resize-none px-4 pt-3 pb-14 text-sm text-white placeholder:text-white/35"
            data-testid="refine-input"
            disabled={busy}
          />
          <div className="absolute right-3 bottom-3 flex items-center gap-2">
            <button
              type="button"
              disabled={busy || !draft.trim()}
              onClick={() => onRebuild()}
              className="btn-gradient inline-flex items-center gap-2 text-sm !py-2 !px-4 disabled:opacity-50"
              data-testid="refine-rebuild-btn"
            >
              {busy ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <SendHorizontal className="h-3.5 w-3.5" />
              )}
              Rebuild
            </button>
          </div>
        </div>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/35">
          Enter to rebuild · Shift+Enter for new line
        </div>
      </div>
    </div>
  );
}
